import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { trackEvent } from '../utils/analytics';

const ResetPassword: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    let status: 'success' | 'error' = 'error';
    try {
      const { data } = await authAPI.confirmPasswordReset(token, password, confirm);
      if (data.success) {
        setDone(true);
        status = 'success';
      } else {
        setError(data.error || 'Unable to reset password.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Invalid or expired link.');
    } finally {
      setLoading(false);
      trackEvent('password_reset_confirm', { status });
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8">
        <div className="max-w-md w-full space-y-4 text-center">
          <h2 className="text-2xl font-semibold text-gray-900">Invalid reset link</h2>
          <p className="text-sm text-gray-600">Please request a new password reset email.</p>
          <Link className="text-blue-600 hover:underline" to="/forgot-password">Request reset</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Set a new password</h2>
          <p className="mt-2 text-center text-sm text-gray-600">Choose a strong password for your account.</p>
        </div>

        {done ? (
          <div className="space-y-4 text-center">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
              Your password has been reset. You can now sign in.
            </div>
            <Link className="text-blue-600 hover:underline" to="/login">Go to sign in</Link>
          </div>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={submit}>
            {error && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
            )}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">New password</label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field mt-1"
                placeholder="Enter new password"
              />
            </div>
            <div>
              <label htmlFor="confirm" className="block text-sm font-medium text-gray-700">Confirm password</label>
              <input
                id="confirm"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="input-field mt-1"
                placeholder="Re-enter new password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:opacity-60"
            >
              {loading ? 'Saving…' : 'Save new password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
